import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { blogIndexQuery } from "@/lib/blog/queries";
import { BlogCard } from "@/components/blog/BlogCard";
import { canonicalUrl } from "@/lib/seo/site";

export const Route = createFileRoute("/blog/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(blogIndexQuery()),
  head: () => ({
    meta: [
      { title: "The CB16 Journal — Fragrance Notes, Guides & Atelier Stories" },
      {
        name: "description",
        content:
          "Perfume guides, ingredient deep-dives and stories from the CB16 atelier in Thane. Learn how to choose, layer and live with fine fragrance.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "The CB16 Journal" },
      {
        property: "og:description",
        content: "Perfume guides, fragrance notes and stories from the CB16 atelier.",
      },
      { property: "og:url", content: canonicalUrl("/blog") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/blog") }],
  }),
  component: BlogIndexPage,
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center text-sm text-foreground/60">
      The journal is unavailable right now. Please try again shortly.
    </div>
  ),
});

function BlogIndexPage() {
  const { data } = useSuspenseQuery(blogIndexQuery());
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("all");
  const [year, setYear] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 9;

  const years = useMemo(
    () =>
      Array.from(
        new Set(data.posts.map((p) => (p.publishedAt ?? "").slice(0, 4)).filter(Boolean)),
      ).sort((a, b) => b.localeCompare(a)),
    [data.posts],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.posts.filter((p) => {
      if (category !== "all" && p.categorySlug !== category) return false;
      if (tag !== "all" && !p.tags.includes(tag)) return false;
      if (year !== "all" && !(p.publishedAt ?? "").startsWith(year)) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.join(" ").toLowerCase().includes(q)
      );
    });
  }, [data.posts, query, category, tag, year]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const featured = data.posts.find((p) => p.isFeatured) ?? data.posts[0];

  const selectClass =
    "h-10 border border-border bg-background px-3 text-[12px] uppercase tracking-[0.14em] text-foreground/70 focus:border-[#C6A15B] focus:outline-none";

  return (
    <main className="bg-background">
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-10 lg:py-20">
          <p className="text-[10px] font-semibold uppercase tracking-[0.34em] text-[#C6A15B]">
            CB16 · Journal
          </p>
          <h1
            className="mx-auto mt-4 max-w-2xl text-4xl font-normal leading-tight text-foreground lg:text-5xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Notes from the Atelier
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/60">
            Guides, ingredient studies and stories on living beautifully with fine fragrance.
          </p>
        </div>
      </section>

      {featured && (
        <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-10">
          <div className="grid gap-8 border border-border bg-card p-6 lg:grid-cols-2 lg:p-8">
            {featured.featuredImage && (
              <img
                src={featured.featuredImage}
                alt={featured.title}
                className="aspect-[16/10] w-full object-cover"
              />
            )}
            <div className="flex flex-col justify-center gap-4">
              <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">
                Featured · {featured.categoryName}
              </span>
              <h2
                className="text-3xl font-normal leading-tight text-foreground"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {featured.title}
              </h2>
              <p className="text-sm leading-7 text-foreground/60">{featured.excerpt}</p>
              <a
                href={`/blog/${featured.slug}`}
                className="w-fit border border-[#C6A15B] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B] transition hover:bg-[#C6A15B] hover:text-white"
              >
                Read the story
              </a>
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search the journal"
            aria-label="Search the journal"
            className="h-10 min-w-[220px] flex-1 border border-border bg-background px-3 text-sm text-foreground placeholder:text-foreground/40 focus:border-[#C6A15B] focus:outline-none"
          />
          <select
            className={selectClass}
            value={category}
            aria-label="Filter by category"
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All categories</option>
            {data.categories.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            className={selectClass}
            value={tag}
            aria-label="Filter by tag"
            onChange={(e) => {
              setTag(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All tags</option>
            {data.tags.map((t) => (
              <option key={t.id} value={t.slug}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            className={selectClass}
            value={year}
            aria-label="Filter by year"
            onChange={(e) => {
              setYear(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {visible.length === 0 ? (
          <p className="py-16 text-center text-sm text-foreground/50">
            No articles match those filters yet.
          </p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p) => (
              <BlogCard key={p.id} post={p} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i + 1)}
                className={
                  "h-9 w-9 border text-[12px] transition " +
                  (safePage === i + 1
                    ? "border-[#C6A15B] bg-[#C6A15B] text-white"
                    : "border-border text-foreground/60 hover:border-[#C6A15B]")
                }
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}